/**
 * planPresentation.ts — Staff LK proxy to MIS ai-presentations.
 */
import type { TenantBootstrapModel } from '@unified-lk/tenant-config'
import type { AuthSession } from '../../../../types/plans'

export type PresentationPhotoSource = {
  photo_id: string
  source: 'plan_media' | 'patient_media' | 'media_library'
  display_name: string
  mime_type: string
  size_bytes: number
  preview_url: string | null
  thumbnail_path: string
  source_key?: string
  source_label?: string
  share_in_public?: boolean
}

export type PresentationPhotoSources = {
  plan_id: string
  patient_id: string
  stages: Array<{ stage_index: number; label: string; services: string[]; teeth: string[] }>
  plan_photos: PresentationPhotoSource[]
  nas_photos: PresentationPhotoSource[]
  nas_sources: Array<{ source_key: string; source_label: string; linked: boolean; available: boolean; asset_count: number; folder_label: string | null }>
}

export type GammaTheme = { id: string; name: string }

export type PhotoLayoutResult = {
  assignments: Array<{ photo_id: string; stage_index: number; category?: string; description?: string }>
  unassigned: string[]
  summary?: string
  photo_count: number
  stages: PresentationPhotoSources['stages']
}

const photoSourcesCache = new Map<string, PresentationPhotoSources>()

function photoSourcesCacheKey(patientId: string, planId: string) {
  return `${patientId}:${planId}`
}

function authHeaders(session: AuthSession) {
  return { Authorization: `Bearer ${session.accessToken}`, 'Content-Type': 'application/json' }
}


export async function fetchGammaThemes(params: {
  bootstrap: TenantBootstrapModel
  session: AuthSession
}): Promise<GammaTheme[]> {
  const res = await fetch(`${params.bootstrap.apiBase}/staff/ai-presentations/themes`, {
    headers: { Authorization: `Bearer ${params.session.accessToken}` },
  })
  if (!res.ok) throw new Error(`gamma-themes ${res.status}`)
  const data = (await res.json()) as { items?: GammaTheme[] }
  return data.items ?? []
}

export async function fetchPresentationPhotoSources(params: {
  bootstrap: TenantBootstrapModel
  session: AuthSession
  patientId: string
  planId: string
}): Promise<PresentationPhotoSources> {
  const cacheKey = photoSourcesCacheKey(params.patientId, params.planId)
  const cached = photoSourcesCache.get(cacheKey)
  if (cached) return cached
  const q = new URLSearchParams({ patient_id: params.patientId, plan_id: params.planId })
  const res = await fetch(`${params.bootstrap.apiBase}/staff/ai-presentations/photo-sources?${q}`, {
    headers: authHeaders(params.session),
  })
  if (!res.ok) throw new Error(`photo-sources ${res.status}`)
  const data = (await res.json()) as PresentationPhotoSources
  photoSourcesCache.set(cacheKey, data)
  return data
}

export function prefetchPresentationPhotoSources(params: {
  bootstrap: TenantBootstrapModel
  session: AuthSession
  patientId: string
  planId: string
}): Promise<PresentationPhotoSources> {
  return fetchPresentationPhotoSources(params)
}

export async function analyzePresentationPhotos(params: {
  bootstrap: TenantBootstrapModel
  session: AuthSession
  patientId: string
  planId: string
  photoIds: string[]
}): Promise<PhotoLayoutResult> {
  const res = await fetch(`${params.bootstrap.apiBase}/staff/ai-presentations/analyze-photos`, {
    method: 'POST',
    headers: authHeaders(params.session),
    body: JSON.stringify({
      tenant_id: params.bootstrap.tenant,
      patient_id: params.patientId,
      plan_id: params.planId,
      photo_ids: params.photoIds,
    }),
  })
  if (!res.ok) throw new Error(`analyze-photos ${res.status}`)
  return res.json() as Promise<PhotoLayoutResult>
}

export async function generatePresentation(params: {
  bootstrap: TenantBootstrapModel
  session: AuthSession
  patientId: string
  planId: string
  selectedPhotoIds?: string[]
  mediaUrls?: string[]
  photoLayout?: PhotoLayoutResult | null
  customPrompt?: string
  themeId?: string
}): Promise<{ generation_id: string }> {
  const res = await fetch(`${params.bootstrap.apiBase}/staff/ai-presentations/generate`, {
    method: 'POST',
    headers: authHeaders(params.session),
    body: JSON.stringify({
      mode: 'constructor_plan',
      tenant_id: params.bootstrap.tenant,
      patient_id: params.patientId,
      plan_id: params.planId,
      provider: 'gamma',
      export_as: 'pdf',
      num_cards: 12,
      ...(params.themeId ? { theme_id: params.themeId } : {}),
      ...(params.customPrompt?.trim() ? { custom_prompt: params.customPrompt.trim() } : {}),
      ...(params.selectedPhotoIds?.length ? { selected_photo_ids: params.selectedPhotoIds } : {}),
      ...(params.mediaUrls?.length ? { media_urls: params.mediaUrls } : {}),
      ...(params.photoLayout ? { photo_layout: params.photoLayout } : {}),
    }),
  })
  if (!res.ok) throw new Error(`generate ${res.status}`)
  return res.json() as Promise<{ generation_id: string }>
}

export async function fetchPresentationStatus(params: {
  bootstrap: TenantBootstrapModel
  session: AuthSession
  generationId: string
}): Promise<{ status: string; gamma_url?: string; error?: string }> {
  const res = await fetch(
    `${params.bootstrap.apiBase}/staff/ai-presentations/status/${params.generationId}`,
    { headers: { Authorization: `Bearer ${params.session.accessToken}` } },
  )
  if (!res.ok) throw new Error(`status ${res.status}`)
  return res.json() as Promise<{ status: string; gamma_url?: string; error?: string }>
}

export type NasFolderBrowseResult = {
  source_key: string
  folder_path: string
  linked_folder_path: string | null
  subfolders: Array<{ folder_path: string; folder_name: string; image_count: number; subfolder_count: number }>
  assets: Array<{ asset_id: string; asset_name: string; mime_type: string; preview_url: string | null }>
  asset_count: number
}

export async function fetchNasFolderBrowse(params: {
  bootstrap: TenantBootstrapModel
  session: AuthSession
  patientId: string
  sourceKey: string
  folderPath?: string
  limit?: number
}): Promise<NasFolderBrowseResult> {
  const q = new URLSearchParams({
    patient_id: params.patientId,
    source_key: params.sourceKey,
    folder_path: params.folderPath ?? '',
    limit: String(params.limit ?? 48),
  })
  const res = await fetch(`${params.bootstrap.apiBase}/staff/ai-presentations/nas-browse?${q}`, {
    headers: authHeaders(params.session),
  })
  if (!res.ok) throw new Error(`nas-browse ${res.status}`)
  return (await res.json()) as NasFolderBrowseResult
}

